import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, BadgePercent, Loader2, CheckCircle2, Gift } from "lucide-react";
import { api } from "../lib/api";

const STORAGE_KEY = "jdm_newsletter_popup_v1";
const PENDING_VOUCHER_KEY = "jdm_pending_voucher_v1";
const DELAY_MS = 4000;
// Don't show on these routes — feels jarring after checkout
const SKIP_PATHS = ["/success", "/cancel"];

const NewsletterPopup = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null); // { code, already, message }

  useEffect(() => {
    if (SKIP_PATHS.includes(pathname) || pathname.startsWith("/admin")) return;
    let stored = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* localStorage blocked */ }
    if (stored) return; // dismissed or subscribed already
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, [pathname]);

  // Listen for explicit "open me" events (footer link, future CTAs).
  // Resets the success state so already-subscribed visitors can re-trigger
  // the form (e.g. they cleared cookies on a new device).
  useEffect(() => {
    const handler = () => {
      setSuccess(null);
      setEmailError("");
      setOpen(true);
    };
    window.addEventListener("jdm:open-newsletter-popup", handler);
    return () => window.removeEventListener("jdm:open-newsletter-popup", handler);
  }, []);

  const persistDismiss = (state) => {
    try { localStorage.setItem(STORAGE_KEY, state); } catch { /* ignore */ }
  };

  const close = () => {
    setOpen(false);
    persistDismiss("dismissed");
  };

  const submit = async (e) => {
    e.preventDefault();
    setEmailError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError("Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: email.trim(),
        first_name: firstName.trim() || null,
        source: "popup",
      });
      setSuccess({
        code: data.voucher_code,
        already: data.already_subscribed,
        message: data.message,
      });
      persistDismiss("subscribed");
      // Remember the code so the cart drawer can nudge them to apply it later.
      try {
        if (data.voucher_code) {
          localStorage.setItem(
            PENDING_VOUCHER_KEY,
            JSON.stringify({ code: data.voucher_code, savedAt: Date.now() })
          );
          window.dispatchEvent(new Event("jdm:pending-voucher-updated"));
        }
      } catch { /* ignore */ }
    } catch (err) {
      setEmailError(err?.response?.data?.detail || "Couldn't sign you up. Try again?");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="newsletter-popup-root"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6"
    >
      <div
        data-testid="newsletter-popup-backdrop"
        onClick={close}
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
      />
      <div
        data-testid="newsletter-popup"
        className="relative w-full max-w-md rounded-3xl border border-[#ff1a8c]/50 bg-[#0a0a10] shadow-[0_0_60px_rgba(255,26,140,0.35)] overflow-hidden"
      >
        {/* gradient accent */}
        <div className="absolute inset-x-0 -top-32 h-64 bg-gradient-to-b from-[#ff1a8c]/30 via-[#ff1a8c]/5 to-transparent pointer-events-none" />

        <button
          data-testid="newsletter-popup-close"
          onClick={close}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-gray-400 hover:text-white hover:border-[#ff1a8c] transition z-10"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative px-7 pt-12 pb-8">
          {!success ? (
            <>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#ff1a8c]/60 text-[10px] uppercase tracking-[0.28em] text-[#ff1a8c] mb-5">
                <BadgePercent className="w-3 h-3" />
                10% off online · Inside scoop
              </span>
              <h2 className="font-script text-5xl sm:text-6xl neon-gradient-text leading-[0.95] mb-2">
                Sweet,<br />you&apos;re new!
              </h2>
              <p className="text-gray-300 text-sm leading-relaxed mt-3 mb-6">
                Get <span className="text-[#00b4ff] font-semibold">10% off your first online order</span> — straight to your inbox. Plus first dibs on new flavours and seasonal drops.
              </p>

              <form onSubmit={submit} className="flex flex-col gap-3">
                <input
                  data-testid="newsletter-firstname-input"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name (optional)"
                  className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#00b4ff] transition"
                  autoComplete="given-name"
                />
                <input
                  data-testid="newsletter-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className={`w-full bg-black/40 border ${
                    emailError ? "border-[#ff1a8c]" : "border-white/15"
                  } rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#00b4ff] transition`}
                  autoComplete="email"
                  required
                />
                {emailError && (
                  <p data-testid="newsletter-error" className="text-xs text-[#ff1a8c]">{emailError}</p>
                )}
                <button
                  data-testid="newsletter-submit-button"
                  type="submit"
                  disabled={loading}
                  className="neon-btn w-full justify-center mt-1"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending your code…
                    </>
                  ) : (
                    "Get my code"
                  )}
                </button>
              </form>

              <p className="text-[10px] text-gray-500 mt-4 text-center tracking-wide">
                No spam. Unsubscribe anytime.
              </p>
            </>
          ) : (
            <div data-testid="newsletter-success" className="text-center py-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00b4ff]/15 border-2 border-[#00b4ff] flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#00b4ff]" />
              </div>
              <h2 className="font-script text-5xl neon-gradient-text leading-none mb-2">
                {success.already ? "Already in!" : "You're in!"}
              </h2>
              <p className="text-gray-300 text-sm mt-3 mb-5">
                {success.already
                  ? "Welcome back — your code still works."
                  : "Check your inbox for your 10% off code."}
              </p>
              <div className="bg-gradient-to-br from-[#ff1a8c]/15 to-[#00b4ff]/10 border-2 border-dashed border-[#ff1a8c] rounded-2xl p-5 mb-5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1">Your code</p>
                <p data-testid="newsletter-voucher-code" className="font-bold text-3xl text-[#ff1a8c] tracking-[0.08em]">
                  {success.code}
                </p>
                <p className="text-xs text-gray-400 mt-2 flex items-center justify-center gap-1.5">
                  <Gift className="w-3 h-3" /> 10% off your online order
                </p>
              </div>
              <button
                data-testid="newsletter-done-button"
                onClick={close}
                className="neon-btn w-full justify-center"
              >
                Sweet, let&apos;s shop
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
